import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import { useQuery } from '@apollo/client';
import { green } from 'Styles/colors';
import * as Styled from '../../CouponDialog.styled';
import CouponDialogSMSForm from '../CouponDialogSmsForm/CouponDialogSmsForm';
import OfferMessage from '../../../OfferMessage/OfferMessage';
import ConfirmationIcon from '../../../Icons/ConfirmationIcon/ConfirmationIcon';
import { GET_USER_ID , GET_USER_LOCALE } from '../../../App.gql';
import { useIdentity } from '../../../Authentication/Authentication.hooks';
import ShopButtons from '../../../ShopButtons/ShopButtons';

/**
 * Coupon Dialog Success displays the success messaging for signing up to our mailing list, a new-user coupon code,
 * and the opportunity to sign up for SMS notifications (US only).
 */
const CouponDialogEmailSuccess = ({
  couponCode,
  discountPercentage,
  isAutoApplied,
  emailAlreadyExists,
  email,
  ...props
}) => {
  const { identifyUser } = useIdentity();

  const { data } = useQuery(GET_USER_ID, {
    variables: { email },
    skip: !emailAlreadyExists
  });
  const bombasId = data?.user?.bombasId;

  const { data: localeData } = useQuery(GET_USER_LOCALE);
  const country = localeData?.user?.locale?.country;

  if (bombasId) {
    identifyUser({ email, bombasId });
  }

  useEffect(() => {
    if (!emailAlreadyExists || isAutoApplied) {
      PubSub.publish('cart.discountApply', couponCode);
    }
  }, []);

  return (
    <>
      {!isAutoApplied && emailAlreadyExists ? (
        <Styled.HeadingSmaller $withBottomMargin >Email Already Used.</Styled.HeadingSmaller>
      ) : (
        <Styled.OfferMessageContainer>
          <OfferMessage
            accentColor={green}
            title={`${discountPercentage}% Off Has Been Applied to Your Bag.`}
            subtitle={`Code ${couponCode}`}
            rightComponent={<ConfirmationIcon />}
          />
        </Styled.OfferMessageContainer>
      )}
      {country === 'US' ? (
        <CouponDialogSMSForm
          bodyHeadingText={
            !isAutoApplied && emailAlreadyExists
              ? 'Want Early Access To Bombas Promotions Instead?'
              : 'Also Want Early Access To Bombas Promotions?'
          }
          emailAlreadyExists={emailAlreadyExists}
          email={email}
          {...props}
        />
      ) : (
        <>
          <Styled.ContentContainer>
            <Styled.HeadingSmaller>Upgrade Your Everyday Good(s)</Styled.HeadingSmaller>
            <Styled.SubHeadingEmailSuccess>Build your collection of daily essentials that both feel good and do good.</Styled.SubHeadingEmailSuccess>
          </Styled.ContentContainer>
          <ShopButtons/>
        </>
      )}
    </>
  );
};

CouponDialogEmailSuccess.propTypes = {
  /**
   * Coupon code to display to the user.
   */
  couponCode: PropTypes.string.isRequired,
  /**
   * Percentage off value of the discount
   */
  discountPercentage: PropTypes.number,
  /**
   * If `true`, show new customer flow, even if user submits an email with previous orders
   */
  isAutoApplied: PropTypes.bool,
  /**
   * 'true' if email has a past order associated to it
   */
  emailAlreadyExists: PropTypes.bool,
  /**
   * the submitted email address
   */
  email: PropTypes.string
};

CouponDialogEmailSuccess.defaultProps = {
  discountPercentage: 20,
  isAutoApplied: false,
  emailAlreadyExists: false,
  email: undefined
};

export default CouponDialogEmailSuccess;
